import { Box, Typography, Chip } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { COLORS } from '../../constants/colors'

const goldPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 8px ${COLORS.gold.glow}, 0 0 20px rgba(255,215,0,0.15);
    border-color: ${COLORS.gold.border};
  }
  50% {
    box-shadow: 0 0 16px ${COLORS.gold.glow}, 0 0 40px rgba(255,215,0,0.25);
    border-color: ${COLORS.gold.primary};
  }
`

const BossCard = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '16px 18px',
  borderRadius: 14,
  border: `2px solid ${COLORS.gold.border}`,
  background: `linear-gradient(135deg, ${COLORS.gold.muted} 0%, rgba(0,0,0,0.4) 100%)`,
  animation: `${goldPulse} 3s ease-in-out infinite`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, transparent, ${COLORS.gold.primary}, transparent)`,
  },
}))

const Mono = styled(Typography)(() => ({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '0.75rem',
}))

/**
 * Elite-tier Boss Man Signal — elevated visual treatment.
 * @param {{ signal: object }} props
 */
export default function BossManSignal({ signal }) {
  const { symbol, token, price, change, rugScore, wallet, conviction, timestamp } = signal
  const positive = (change ?? 0) >= 0

  return (
    <BossCard>
      {/* Boss avatar */}
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          border: `2px solid ${COLORS.gold.primary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          background: COLORS.gold.muted,
          flexShrink: 0,
        }}
      >
        👑
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: COLORS.gold.primary }}>
            Boss Man Signal
          </Typography>
          {conviction && (
            <Chip
              label={`${conviction}% conviction`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                fontWeight: 700,
                color: COLORS.gold.primary,
                background: COLORS.gold.muted,
                border: `1px solid ${COLORS.gold.border}`,
              }}
            />
          )}
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
          {symbol || token || 'Unknown Token'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {price != null && <Mono sx={{ color: COLORS.text.secondary }}>${Number(price).toFixed(6)}</Mono>}
          {change != null && (
            <Mono sx={{ color: positive ? COLORS.green.primary : COLORS.red.primary }}>
              {positive ? '+' : ''}{Number(change).toFixed(2)}%
            </Mono>
          )}
          {rugScore != null && (
            <Mono sx={{ color: rugScore >= 70 ? COLORS.red.primary : rugScore >= 40 ? COLORS.gold.primary : COLORS.green.primary }}>
              Rug: {rugScore}
            </Mono>
          )}
          {wallet && (
            <Mono sx={{ color: COLORS.text.muted }}>
              {wallet.slice(0, 4)}…{wallet.slice(-4)}
            </Mono>
          )}
        </Box>
      </Box>

      <Mono sx={{ color: COLORS.gold.dark, fontSize: '0.65rem', flexShrink: 0 }}>
        {timestamp ? new Date(timestamp).toLocaleTimeString() : ''}
      </Mono>
    </BossCard>
  )
}
